import {
  FlexMergeImageRequest,
  GridMergeImageRequest,
  MergeImageRequest,
  Orientation,
} from '@imagise/generated-imagise-cloud-api-client';
import { EditorMergeParams } from './editor-domain.models';
import { Size2D } from './interfaces/shared';
import { resolveMergeGridShape, resolveMergeLayout } from './merge-layout';

/**
 * The merge result replaces the editor source image, so it is always
 * requested lossless; the final output format is picked in the download
 * dialog like for every other tool.
 */
export const MERGE_OUTPUT_FORMAT = 'png';

export type MergeApiCall =
  | { kind: 'grid'; request: GridMergeImageRequest }
  | { kind: 'flex'; request: FlexMergeImageRequest }
  | { kind: 'single'; request: MergeImageRequest };

/**
 * Maps the merge draft onto the narrowest /merge/to-single-page endpoint.
 * Spacing or a border force the manual-layout endpoint: the grid/flex sugar
 * endpoints compute edge-to-edge bounds on the backend and ignore the
 * request-level margin/border fields entirely.
 */
export function buildMergeApiCall(
  params: EditorMergeParams,
  sizes: readonly Size2D[],
): MergeApiCall {
  const base = {
    toFormat: MERGE_OUTPUT_FORMAT,
    imageBase64Files: params.images.map(image => image.sourceBase64),
    backgroundColor: params.backgroundColor,
  };
  const isPureSugarLayout =
    params.layout !== 'custom' && params.spacing === 0 && params.borderWidth === 0;

  if (isPureSugarLayout && params.layout === 'grid') {
    return {
      kind: 'grid',
      request: {
        ...base,
        ...resolveMergeGridShape(params, params.images.length),
      },
    };
  }

  if (isPureSugarLayout) {
    return {
      kind: 'flex',
      request: {
        ...base,
        orientation:
          params.layout === 'horizontal'
            ? Orientation.Horizontal
            : Orientation.Vertical,
      },
    };
  }

  const layout = resolveMergeLayout(params, sizes);
  const borderWidth = Math.max(0, Math.round(params.borderWidth || 0));

  return {
    kind: 'single',
    request: {
      ...base,
      imageLayoutOptions: layout.tiles.map(tile => ({
        bounds: tile.bounds,
        imageIndex: tile.imageIndex,
        ...(borderWidth > 0
          ? {
              borderWidth,
              borderColor: params.borderColor,
            }
          : {}),
      })),
    },
  };
}
